// src/contexts/CustomerAuthContext.tsx
// Manages the authenticated customer session.
// The auth JWT is stored in localStorage under a stable, environment-scoped key.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { scopedStorageKey } from "./useLocalStorage";

// Environment-scoped: a sandbox JWT is signed for a different Square account,
// so localhost must not read (or write) the live site's session. See
// scopedStorageKey — the production hostnames keep the unsuffixed key.
const STORAGE_KEY = scopedStorageKey("americana-customer-auth");

// Admin status is decided SERVER-SIDE (customer-verify-otp.js checks the email
// against ADMIN_EMAILS) and handed back on the session as `isAdmin`. The admin
// address is deliberately NOT in this bundle: a REACT_APP_* value is compiled
// into the public JS, so the old allowlist-in-the-client approach published the
// owner's login address to anyone who opened devtools.
//
// This flag is UX ONLY — it decides whether to render the admin nav/route. Real
// enforcement lives in netlify/functions/utils/admin-auth.js, which re-verifies
// the signed JWT on every admin request. A tampered localStorage session can
// flip this boolean but still can't call any admin endpoint.

export interface CustomerSession {
  authToken: string;
  customerId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  /** Server-decided admin flag (see note at top of file). Absent on sessions
   *  stored before this claim existed — those users re-log in to get it. */
  isAdmin?: boolean;
  savedAddress?: {
    addressLine1?: string;
    addressLine2?: string;
    locality?: string;
    administrativeDistrictLevel1?: string;
    postalCode?: string;
    country?: string;
  };
}

interface CustomerAuthContextValue {
  session: CustomerSession | null;
  isLoggedIn: boolean;
  /** UX-only flag: is the logged-in email on the admin allowlist? Server still
   *  re-checks the JWT on every admin request. */
  isAdmin: boolean;
  login: (
    authToken: string,
    customer: Omit<CustomerSession, "authToken">,
  ) => void;
  logout: () => void;
  refreshSession: (updates: Partial<CustomerSession>) => void;
  /**
   * Was the session dropped because its token lapsed, rather than because the
   * customer asked to sign out?
   *
   * Expiry is the one sign-out nobody initiated, so it's the one that has to
   * explain itself. It's mostly invisible — the account pages just redirect to
   * sign-in, which reads as normal — but at checkout it silently removes the
   * cards on file and swaps the saved-card list for the card entry form, with
   * nothing on screen to say why the card the buyer expected is gone.
   *
   * Cleared by a fresh `login` (and by an explicit `logout`, which is a
   * different thing and needs no explanation), or by `clearSessionExpired`
   * once whatever surfaced it has had its say.
   */
  sessionExpired: boolean;
  /**
   * Acknowledge the expiry without signing back in.
   *
   * The flag lives in a context that outlives every page, so without this it
   * stays true for the rest of the browsing session: a buyer who reads the
   * notice, shrugs, and checks out as a guest keeps being told their session
   * expired on every subsequent visit to checkout — including the receipt-to-
   * cart round trip of a second order. It's a one-time explanation, so it needs
   * a way to be finished with.
   */
  clearSessionExpired: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(
  null,
);

/**
 * Read the `exp` claim out of the auth JWT, in seconds. `null` when it can't be
 * read for any reason.
 *
 * Decoding only — the signature is NOT checked, and can't be: the HMAC key is
 * server-only (utils/auth.js), which is the whole point. That's fine, because
 * nothing here grants access. Every endpoint re-verifies the token properly, so
 * this is purely about not showing someone a signed-in UI backed by a token the
 * server will refuse.
 */
function readTokenExpiry(authToken: string): number | null {
  try {
    const body = authToken.split(".")[1];
    if (!body) return null;
    // base64url → base64, then re-pad. atob rejects both differences.
    const padded =
      body.replace(/-/g, "+").replace(/_/g, "/") +
      "=".repeat((4 - (body.length % 4)) % 4);
    const binary = atob(padded);
    // The payload carries names and an email, so it can hold multibyte UTF-8.
    // atob yields a *binary* string, and JSON.parse on that mangles anything
    // non-ASCII into a parse failure — which would read as "no expiry" and quietly
    // defeat this check for exactly the buyers with an accent in their name.
    const json =
      typeof TextDecoder !== "undefined"
        ? new TextDecoder().decode(
            Uint8Array.from(binary, (c) => c.charCodeAt(0)),
          )
        : binary;
    const exp = JSON.parse(json)?.exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/**
 * Has this session's token passed its expiry?
 *
 * Deliberately asymmetric: a token we can't decode is treated as VALID, not
 * expired. Being wrong in that direction costs one rejected request (the server
 * is the real authority and the UI recovers); being wrong the other way would
 * sign out every customer at once the moment a decoding bug shipped. Only a
 * successfully-read `exp` that has genuinely passed drops the session.
 */
export function isSessionExpired(
  session: Pick<CustomerSession, "authToken">,
): boolean {
  const exp = readTokenExpiry(session.authToken);
  return exp !== null && exp * 1000 <= Date.now();
}

/** setTimeout coerces anything past ~24.8 days to fire immediately, which would
 *  spin. Tokens are 30-day, so most of a session's life is beyond this. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Read once, on mount. Returns the expiry verdict alongside the session
  // because the two are decided together and the caller needs both — splitting
  // them into two initializers would parse (and judge) the token twice.
  const [bootstrap] = useState<{
    session: CustomerSession | null;
    expired: boolean;
  }>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return { session: null, expired: false };
      const parsed = JSON.parse(stored) as CustomerSession;
      // A stored session outlives its token: the JWT is 30-day, but nothing
      // here used to look at `exp`, so day 31 still rendered a signed-in UI
      // backed by a token every endpoint rejects. The failure was silent in the
      // worst way — account endpoints answer 200-with-empty-data rather than
      // 401, so the buyer saw no order history and no saved cards rather than
      // "please sign in", and at checkout the upsert fell through to the guest
      // email search and could bind the order to a different record. Drop it
      // here and they simply get the sign-in they were due.
      return isSessionExpired(parsed)
        ? { session: null, expired: true }
        : { session: parsed, expired: false };
    } catch {
      /* ignore corrupt storage */
    }
    return { session: null, expired: false };
  });

  const [session, setSession] = useState<CustomerSession | null>(
    bootstrap.session,
  );
  // Shows exactly once per lapse: the sync effect below removes the storage key
  // as soon as the session goes, so the NEXT load finds nothing stored and
  // starts false rather than re-announcing an expiry the buyer already saw.
  const [sessionExpired, setSessionExpired] = useState(bootstrap.expired);

  // Keep localStorage in sync whenever session changes
  useEffect(() => {
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [session]);

  // The load-time check above only covers a fresh page load. This site is an
  // SPA that people leave open — a tab parked for a week can cross the expiry
  // without ever remounting — so re-check when the tab comes back to the
  // foreground, and arm a timer for the exact moment when it's near enough to
  // schedule. Without this the first sign the buyer gets is a checkout that
  // quietly behaves as a guest.
  useEffect(() => {
    if (!session) return;

    // `session` is safe to close over: this effect re-subscribes on every
    // session change, so the listeners and timer below are always bound to the
    // current one.
    const check = () => {
      if (!isSessionExpired(session)) return;
      setSession(null);
      // Records WHY the session went, so checkout can say so. Without this the
      // buyer's cards on file just vanish and the saved-card list is replaced
      // by the card form with nothing on screen to explain it.
      setSessionExpired(true);
    };

    check();

    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", check);

    // Fires the session out from under an idle tab the moment it lapses, rather
    // than waiting for the buyer to touch something.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const exp = readTokenExpiry(session.authToken);
    if (exp !== null) {
      // +1s so the timer lands just PAST the boundary; firing exactly on it
      // leaves `exp * 1000 <= Date.now()` marginal and can cost a re-arm.
      const delay = exp * 1000 - Date.now() + 1000;
      if (delay > 0 && delay <= MAX_TIMEOUT_MS)
        timer = setTimeout(check, delay);
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", check);
      if (timer) clearTimeout(timer);
    };
  }, [session]);

  const login = useCallback(
    (authToken: string, customer: Omit<CustomerSession, "authToken">) => {
      setSession({ authToken, ...customer });
      // They've signed back in — the notice has served its purpose and would
      // otherwise sit there contradicting the signed-in panel right above it.
      setSessionExpired(false);
    },
    [],
  );

  const logout = useCallback(() => {
    setSession(null);
    // An explicit sign-out is not an expiry. Clearing here also covers the
    // buyer who signs in after a lapse and then hits "Not you?".
    setSessionExpired(false);
  }, []);

  const refreshSession = useCallback((updates: Partial<CustomerSession>) => {
    setSession((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  return (
    <CustomerAuthContext.Provider
      value={{
        session,
        isLoggedIn: !!session,
        isAdmin: !!session?.isAdmin,
        login,
        logout,
        refreshSession,
        sessionExpired,
        clearSessionExpired,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = (): CustomerAuthContextValue => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx)
    throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
};
